import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  useCheckUsernameMutation,
  useUpdateUsernameMutation,
  useUserGenerateUsernameQuery,
} from "services/graphql";
import { checkUsernameValidity } from "helpers/checkUsernameValidity";
import { useOnboardingAnalytics } from "@/analytics";
import { useGetUserProfile } from "hooks/User/useGetUserProfile";
import { UserData } from "../../constants";
import styles from "../../styles.module.scss";

export interface UsernameStepProps {
  onComplete: (data: Pick<UserData, "username">) => void;
  name: string;
}

const DEBOUNCE_DELAY = 1000;

export const UsernameStep: React.FC<UsernameStepProps> = ({
  onComplete,
  name,
}) => {
  const [username, setUsername] = useState("");
  const [debouncedUsername, setDebouncedUsername] = useState("");
  const [error, setError] = useState("");
  const [isAvailable, setIsAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  const { profileData } = useGetUserProfile();
  const [checkUsername] = useCheckUsernameMutation();
  const [updateUsername] = useUpdateUsernameMutation();
  const {
    setUsernameAnalytics,
    onboardingErrorAnalytics,
  } = useOnboardingAnalytics();

  const { data: generatedData } = useUserGenerateUsernameQuery({
    fetchPolicy: "network-only",
    variables: { input: { name: name || "" } },
    skip:
      !name || (!profileData?.isFirebaseUsername && !!profileData?.username),
  });

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.classList.add(styles.animate);
    }
  }, []);

  useEffect(() => {
    if (!profileData?.isFirebaseUsername && profileData?.username) {
      setUsername(profileData.username);
      setIsAvailable(true);
    } else if (generatedData?.userGenerateUsername?.username) {
      setUsername(generatedData.userGenerateUsername.username);
      setDebouncedUsername(generatedData.userGenerateUsername.username);
    }
  }, [generatedData, profileData]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedUsername(username);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [username]);

  useEffect(() => {
    // Always validate the username format regardless of existing username
    if (!debouncedUsername) {
      return;
    }

    const { isValid, message } = checkUsernameValidity(debouncedUsername);

    if (!isValid) {
      setIsAvailable(false);
      setError(message);
      return;
    }

    // If user is keeping their existing username, mark as available without checking
    if (
      !profileData?.isFirebaseUsername &&
      profileData?.username &&
      profileData.username === debouncedUsername
    ) {
      setIsAvailable(true);
      setError("");
      return;
    }

    // Otherwise check availability via API
    setIsChecking(true);
    checkUsername({
      variables: { input: { username: debouncedUsername } },
    })
      .then(({ data }) => {
        if (data?.userCheckUsername.exists) {
          setIsAvailable(false);
          setError("This username is invalid or already taken");
        } else {
          setIsAvailable(true);
          setError("");
        }
      })
      .catch(() => {
        setError("Username is invalid, please try again");
        setIsAvailable(false);
      })
      .finally(() => {
        setIsChecking(false);
      });
  }, [debouncedUsername, profileData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username) {
      setError("Please enter a username");
      return;
    }

    // If user already has a username and it's not a Firebase username, just continue
    if (
      !profileData?.isFirebaseUsername &&
      profileData?.username === username
    ) {
      onComplete({ username });
      return;
    }

    if (!isAvailable) {
      setError("Please choose an available username");
      return;
    }

    const isGenerated =
      generatedData?.userGenerateUsername?.username === username;
    setUsernameAnalytics(username, isGenerated);

    try {
      await updateUsername({
        variables: {
          input: {
            username,
          },
        },
      });
      onComplete({ username });
    } catch (err) {
      onboardingErrorAnalytics(
        err instanceof Error ? err.message : "Unknown error"
      );
      setError("Failed to update username. Please try again.");
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
    setUsername(value);
    setError("");
    setIsAvailable(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <h1 className={styles.title}>Set Username</h1>
      <p className={styles.subtitle}>
        {!profileData?.isFirebaseUsername && profileData?.username
          ? "Continue with your existing username or set a new one."
          : "Add a username or use our suggestion. You can change this at any time."}
      </p>

      <form onSubmit={handleSubmit}>
        <input
          type="text"
          value={username}
          onChange={handleUsernameChange}
          className={styles.input}
          placeholder="Enter username"
          maxLength={24}
          autoCapitalize="none"
        />

        <div className={styles.validationContainer}>
          {error && <p className={styles.error}>{error}</p>}
          {isChecking &&
            username &&
            (!profileData?.username || profileData?.isFirebaseUsername) && (
              <p className={styles.validationMessage}>
                Checking availability...
              </p>
            )}
          {isAvailable && username && !isChecking && (
            <p className={styles.success}>
              {!profileData?.isFirebaseUsername &&
              profileData?.username === username
                ? "This is your current username"
                : "This username is currently available"}
            </p>
          )}
          <span className={styles.characterCount}>{username.length}/24</span>
        </div>

        <button
          type="submit"
          className={styles.button}
          disabled={isChecking || !isAvailable || !username || !!error}
        >
          CONTINUE
        </button>
      </form>
    </motion.div>
  );
};
